import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Calendar, Plus, Edit2, Trash2, Gift } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const ADMIN_ROLES = ['admin', 'super-admin', 'hr-manager', 'account-manager'];

const ConfirmModal = ({ show, onHide, onConfirm, loading, title, message }) => (
    <Modal show={show} onHide={onHide} centered size="sm">
        <Modal.Header closeButton className="border-0"><Modal.Title className="fw-bold fs-6">{title}</Modal.Title></Modal.Header>
        <Modal.Body className="text-center py-3"><p className="text-muted mb-0" style={{ fontSize: 14 }}>{message}</p></Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onHide} disabled={loading}>Cancel</Button>
            <Button variant="danger" onClick={onConfirm} disabled={loading}>
                {loading && <Spinner size="sm" className="me-1" />}Yes, Delete
            </Button>
        </Modal.Footer>
    </Modal>
);

const HolidayCalendar = () => {
    const { user } = useAuth();
    const isAdmin   = ADMIN_ROLES.includes(user?.role);
    const [year,    setYear]    = useState(new Date().getFullYear());
    const [holidays,setHolidays]= useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm,setShowForm]= useState(false);
    const [editing, setEditing] = useState(null);
    const [form,    setForm]    = useState({ holiday_name:'', holiday_date:'', description:'', is_mandatory:true });
    const [errors,  setErrors]  = useState({});
    const [saving,  setSaving]  = useState(false);
    const [delModal,setDelModal]= useState({ show:false, id:null, loading:false });

    const fetchHolidays = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/hr/holidays', { params: { year } });
            setHolidays(res.data.data || []);
        } catch { toast.error('Failed to load holidays'); }
        finally { setLoading(false); }
    }, [year]);

    useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

    const openAdd = () => { setEditing(null); setForm({ holiday_name:'', holiday_date:'', description:'', is_mandatory:true }); setErrors({}); setShowForm(true); };
    const openEdit = (h) => { setEditing(h); setForm({ holiday_name:h.holiday_name, holiday_date:h.holiday_date?.slice(0,10), description:h.description||'', is_mandatory:h.is_mandatory!==0 }); setErrors({}); setShowForm(true); };

    const validate = () => {
        const e = {};
        if (!form.holiday_name?.trim()) e.holiday_name = 'Holiday name is required';
        if (!form.holiday_date) e.holiday_date = 'Date is required';
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setSaving(true);
        try {
            if (editing) {
                await axiosInstance.put(`/hr/holidays/${editing.id}`, form);
                toast.success('Holiday updated');
            } else {
                await axiosInstance.post('/hr/holidays', form);
                toast.success('Holiday added');
            }
            setShowForm(false);
            fetchHolidays();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        setDelModal(p => ({ ...p, loading: true }));
        try {
            await axiosInstance.delete(`/hr/holidays/${delModal.id}`);
            toast.success('Holiday deleted');
            setDelModal({ show:false, id:null, loading:false });
            fetchHolidays();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Delete failed');
            setDelModal(p => ({ ...p, loading:false }));
        }
    };

    const upcoming = holidays.filter(h => new Date(h.holiday_date) >= new Date());
    const past     = holidays.filter(h => new Date(h.holiday_date) <  new Date());

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1"><Calendar size={22} className="me-2 text-primary" />Holiday Calendar</h4>
                    <p className="text-muted small mb-0">{holidays.length} holidays in {year}</p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ width: 110 }}>
                        {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </Form.Select>
                    {isAdmin && (
                        <Button variant="primary" onClick={openAdd}>
                            <Plus size={18} className="me-1" />Add Holiday
                        </Button>
                    )}
                </div>
            </div>

            {upcoming.length > 0 && (
                <Alert variant="info" className="d-flex align-items-center gap-2 small mb-3 border-0 rounded-3">
                    <Gift size={16} className="flex-shrink-0 text-primary" />
                    <span>Next holiday: <strong>{upcoming[0].holiday_name}</strong> on {new Date(upcoming[0].holiday_date).toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}</span>
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : (
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Holiday</th>
                                <th>Date</th>
                                <th>Day</th>
                                <th>Type</th>
                                <th>Description</th>
                                {isAdmin && <th className="text-end pe-4">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {holidays.length > 0 ? holidays.map(h => {
                                const d       = new Date(h.holiday_date);
                                const isPast  = d < new Date();
                                return (
                                    <tr key={h.id} style={{ opacity: isPast ? 0.6 : 1 }}>
                                        <td className="ps-4 fw-bold">{h.holiday_name}</td>
                                        <td>{d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</td>
                                        <td>{d.toLocaleDateString('en-IN', { weekday:'long' })}</td>
                                        <td>
                                            <Badge bg={h.is_mandatory ? 'danger' : 'secondary'}>
                                                {h.is_mandatory ? 'Mandatory' : 'Optional'}
                                            </Badge>
                                        </td>
                                        <td className="text-muted">{h.description || '—'}</td>
                                        {isAdmin && (
                                            <td className="text-end pe-4">
                                                <div className="d-flex justify-content-end gap-1">
                                                    <Button size="sm" variant="outline-secondary" className="py-0" onClick={() => openEdit(h)}><Edit2 size={13} /></Button>
                                                    <Button size="sm" variant="outline-danger" className="py-0" onClick={() => setDelModal({ show:true, id:h.id, loading:false })}><Trash2 size={13} /></Button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            }) : (
                                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center py-5 text-muted">
                                    No holidays found for {year}.
                                    {isAdmin && <> <Button variant="link" size="sm" onClick={openAdd}>Add one</Button></>}
                                </td></tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Card>

            {/* Add/Edit Modal */}
            <Modal show={showForm} onHide={() => setShowForm(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">{editing ? 'Edit Holiday' : 'Add Holiday'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col xs={12}>
                                <Form.Label className="small fw-bold">Holiday Name *</Form.Label>
                                <Form.Control value={form.holiday_name} placeholder="e.g. Diwali"
                                    onChange={e => { setForm(p=>({...p,holiday_name:e.target.value})); setErrors(p=>({...p,holiday_name:''})); }}
                                    isInvalid={!!errors.holiday_name} />
                                <Form.Control.Feedback type="invalid">{errors.holiday_name}</Form.Control.Feedback>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Form.Label className="small fw-bold">Date *</Form.Label>
                                <Form.Control type="date" value={form.holiday_date}
                                    onChange={e => { setForm(p=>({...p,holiday_date:e.target.value})); setErrors(p=>({...p,holiday_date:''})); }}
                                    isInvalid={!!errors.holiday_date} />
                                <Form.Control.Feedback type="invalid">{errors.holiday_date}</Form.Control.Feedback>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Form.Label className="small fw-bold">Type</Form.Label>
                                <Form.Select value={form.is_mandatory ? 'mandatory' : 'optional'}
                                    onChange={e => setForm(p=>({...p,is_mandatory:e.target.value==='mandatory'}))}>
                                    <option value="mandatory">Mandatory</option>
                                    <option value="optional">Optional</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12}>
                                <Form.Label className="small fw-bold">Description</Form.Label>
                                <Form.Control as="textarea" rows={2} placeholder="Optional note…"
                                    value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} />
                            </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-4">
                            <Button variant="light" type="button" className="flex-grow-1" onClick={() => setShowForm(false)}>Cancel</Button>
                            <Button variant="primary" type="submit" className="flex-grow-1 fw-bold" disabled={saving}>
                                {saving ? <Spinner size="sm" /> : editing ? 'Save Changes' : 'Add Holiday'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <ConfirmModal show={delModal.show} onHide={() => setDelModal({ show:false, id:null, loading:false })}
                onConfirm={handleDelete} loading={delModal.loading}
                title="Delete Holiday" message="Are you sure you want to delete this holiday?" />
        </div>
    );
};

export default HolidayCalendar;
